import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../lib/firebase';
import { collection, addDoc, onSnapshot, query, where, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { secureLog } from '../utils/securityUtils';

const functions = getFunctions(app, 'europe-west1');

export const createCheckoutSession = async (
  userId: string, 
  billingInterval: 'monthly' | 'yearly',
  plan: 'premium' | 'expert' = 'premium'
) => {
  try {
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        billingInterval,
        plan
      }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    window.location.href = data.url;
  } catch (error) {
    secureLog.error('Error creating checkout session:', error);
    throw error;
  }
};

export const getCustomerPortalSession = async (userId: string) => {
  try {
    secureLog.log('Creating portal session for user:', userId);
    const createPortalLink = httpsCallable(
      functions,
      'ext-firestore-stripe-payments-532d-createPortalLink'
    );

    const { data } = await createPortalLink({
      returnUrl: window.location.origin + '/pricing',
      locale: 'fr'
    });

    secureLog.log('Portal session response:', data);

    if (typeof data === 'object' && data !== null && 'url' in data) {
      return { url: data.url as string };
    }

    throw new Error('Invalid portal session response');
  } catch (error) {
    secureLog.error('Error creating customer portal session:', error);
    throw error;
  }
};

export const getSubscriptionDetails = async (userId: string) => {
  try {
    secureLog.log('Getting subscription details for user:', userId);
    
    // D'abord, obtenir le stripeCustomerId de l'utilisateur
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      secureLog.log('User not found');
      return null;
    }

    const userData = userDoc.data();
    secureLog.log('User data from getSubscriptionDetails:', userData);
    
    // Vérifier si l'utilisateur a une licence à vie
    const isLifetime = userData?.isLifetime === true || userData?.isLifetime === 'true';
    secureLog.log('User has lifetime license:', isLifetime);
    
    if (isLifetime) {
      // Retourner un objet de souscription simulé pour les utilisateurs lifetime
      return {
        status: 'active',
        planName: 'Lifetime Premium',
        currentPeriodEnd: new Date('2125-01-01'), // Date très lointaine
        isLifetime: true
      };
    }
    
    const stripeCustomerId = userData?.stripeCustomerId || userData?.customerId;
    
    if (!stripeCustomerId) {
      secureLog.log('No stripe customer ID found for user');
      return null;
    }

    secureLog.log('Using stripe customer ID:', stripeCustomerId);

    // Ensuite, chercher la souscription avec le stripeCustomerId
    const subscriptionsRef = collection(db, 'customers', stripeCustomerId, 'subscriptions');
    const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing']));
    const snapshot = await getDocs(q);
    
    secureLog.log('Subscription snapshot:', {
      empty: snapshot.empty,
      size: snapshot.size,
      docs: snapshot.docs.map(doc => ({
        id: doc.id,
        data: doc.data()
      }))
    });
    
    if (snapshot.empty) {
      secureLog.log('No active subscription found');
      return null;
    }

    const subscription = snapshot.docs[0].data();
    secureLog.log('Found subscription:', subscription);
    
    // Vérifier si l'utilisateur est premium dans son document
    if (userData.isPremium) {
      secureLog.log('User is marked as premium in users collection');
      return {
        status: 'active',
        currentPeriodEnd: userData.premiumEndDate?.toDate(),
        planName: 'Pro Plan',
        cancelAtPeriodEnd: false
      };
    }

    // Si l'utilisateur n'est pas marqué comme premium mais a une souscription active,
    // mettre à jour son statut premium
    if (subscription.status === 'active') {
      await updateDoc(doc(db, 'users', userId), {
        isPremium: true,
        premiumStartDate: subscription.current_period_start,
        premiumEndDate: subscription.current_period_end,
        stripeCustomerId: stripeCustomerId
      });
    }
    
    return {
      status: subscription.status,
      currentPeriodEnd: subscription.current_period_end?.toDate(),
      planName: subscription.items?.[0]?.price?.product?.name || 'Pro Plan',
      cancelAtPeriodEnd: subscription.cancel_at_period_end
    };
  } catch (error) {
    secureLog.error('Error getting subscription details:', error);
    return null;
  }
};

export const checkSubscriptionStatus = async (userId: string) => {
  try {
    const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
    const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing']));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    secureLog.error('Error checking subscription status:', error);
    return false;
  }
};
